import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { get, merge, forEach } from 'lodash';
import { message } from 'antd';
import storage from './localStorage';
import trackEvent from './eventTracker';
import { logout, saveTokens } from '@stores/auth';
import api from '@constants/endpoints';
import errorMesages from '@constants/errorMessages';
import qs from 'qs';
import appConfig from '@config/app';
import BASIC_AUTH_TOKEN from '@constants/basicAuthToken';
import { changeParams } from '@helpers/navigation';
import LocationState from '@interfaces/LocationState';
// const ERROR_MESSAGE_MAX_LENGTH = 100;

export interface AdditionalOptions extends AxiosRequestConfig {
  urlParams?: Record<string, string | undefined>;
}

export interface ClientOptions {
  catch?: boolean;
  isNotFoundRedirect?: boolean;
}

const instance = axios.create({
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  baseURL: appConfig.apiUrl,
});

const refreshInstance = axios.create({
  baseURL: appConfig.apiUrl,
});

const getAccessToken = (requestOptions: AxiosRequestConfig) => {
  const basicAuthToken = get(requestOptions, ['headers', 'Authorization'], '').includes('Basic')
    ? requestOptions.headers.Authorization
    : null;

  const bearerAuthToken = get(storage.readObject('tokens'), ['access_token'], null);

  if (bearerAuthToken && !basicAuthToken) {
    return `Bearer ${bearerAuthToken}`;
  }

  if (basicAuthToken) {
    return basicAuthToken;
  }
};

instance.interceptors.request.use((request) => {
  request.headers['Authorization'] = getAccessToken(request);
  return request;
});

const checkStatus = (response: AxiosResponse) => {
  if (response.status >= 200 && response.status < 300) {
    return;
  }
  throw new Error(response.statusText);
};

const refreshAuthLogic = async (failedRequest: any) => {
  const refresh_token = storage.readObject('tokens').refresh_token;
  const Authorization = BASIC_AUTH_TOKEN;
  const body = qs.stringify({
    grant_type: 'admin_refresh_token',
    refresh_token,
  });
  const refreshOptions = merge({}, api.auth.getToken, {
    headers: { Authorization },
    data: body,
  });
  try {
    const refreshResponse = await refreshInstance(refreshOptions);
    const newTokens = refreshResponse.data;
    storage.writeObject('tokens', newTokens);
    saveTokens(newTokens);
    const newAccessToken = get(newTokens, ['access_token'], null);
    failedRequest.response.config.headers['Authorization'] = `Bearer ${newAccessToken}`;
    return Promise.resolve();
  } catch (error) {
    logout();
  }
};

createAuthRefreshInterceptor(instance, refreshAuthLogic);

async function request(
  options: AxiosRequestConfig,
  additionalOptions?: AdditionalOptions,
  clientOptions: ClientOptions = {
    catch: true,
    isNotFoundRedirect: false,
  },
) {
  // TODO Удалить когда получение несуществующего пользователя по id будет возаращать 404 ошибку (сейчас возвращает успешный ответ с body: null)
  // https://gameram.youtrack.cloud/issue/BACK-690
  const isGetUserByIdUrl = options.url === api.users.getItem.url;
  const mergedOptions = merge({}, options);

  if (additionalOptions) {
    const { urlParams, ...rest } = additionalOptions;
    if (urlParams) {
      forEach(urlParams, (paramValue, paramName) => {
        mergedOptions.url = paramValue
          ? mergedOptions.url?.replace(`{${paramName}}`, paramValue)
          : mergedOptions.url;
      });
    }

    merge(mergedOptions, rest);
  }

  try {
    const response = await instance(mergedOptions);
    checkStatus(response);
    await trackEvent(options, additionalOptions);

    // TODO Удалить когда получение несуществующего пользователя по id будет возаращать 404 ошибку (сейчас возвращает успешный ответ с body: null)
    // https://gameram.youtrack.cloud/issue/BACK-690
    if (clientOptions.isNotFoundRedirect && response.data.body === null && isGetUserByIdUrl) {
      const state: LocationState = {
        errorPageInfo: {
          responseStatus: 404,
        },
      };
      changeParams(null, state);
    }

    return response;
  } catch (error) {
    const errorStatus = get(error, 'response.status');
    const errorStatusText = get(error, 'response.statusText');
    const errorHumanMessage = get(error, 'response.data.errorHumanMessage');
    const errorHumanText = get(error, 'response.data.message');
    const errorDescription = get(error, 'response.data.error_description');

    const errorMessage =
      errorStatus && errorMesages[errorStatus as string]
        ? `${errorStatus}: ${errorMesages[errorStatus as string]} - ${
            errorHumanText || errorHumanMessage || errorDescription
          }`
        : `${errorStatus}: ${errorStatusText} - ${
            errorHumanText || errorHumanMessage || errorDescription
          }` || 'Undefined Error';

    if (clientOptions.catch) {
      message.error(errorMessage);
    }

    if (clientOptions.isNotFoundRedirect && errorStatus === 404) {
      const state: LocationState = {
        errorPageInfo: {
          responseStatus: errorStatus,
        },
      };
      changeParams(null, state);
    }
    throw new Error(errorMessage);
  }
}

export default request;
